import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

/**
 * Change requests against a listing's Internal Details & Costs, raised after
 * FINALISE COST and awaiting the store owner.
 *
 * The section itself is unchanged before costs are finalised — it saves
 * directly, as it always has. These hooks only drive the locked state.
 */

/** Anything outstanding on one listing — drives the section's banner. */
export const useListingChangeRequests = (listingId, enabled = true) =>
  useQuery({
    queryKey: ["listing-change-requests", listingId],
    enabled: !!listingId && enabled,
    queryFn: async () => {
      const { data } = await axios.get(`/api/listings/${listingId}/change-requests`, {
        // The section renders for everyone; only owners can action these. A
        // non-owner seeing a 403 toast on every open would be noise.
        metadata: { silent: true },
      });
      return data?.items ?? [];
    },
  });

/** Was this query refused by the server (as opposed to failing transiently)? */
const isForbidden = (error) => error?.response?.status === 403;

/** The store owner's pending queue — the header bell. */
export const usePendingChangeRequests = (enabled = true) =>
  useQuery({
    queryKey: ["listing-change-requests", "pending"],
    enabled,
    // The bell is ambient: poll rather than make the owner reload to notice a
    // request. Slow enough not to matter, fast enough to be useful.
    //
    // ...but STOP once the server says 403. The caller-side gate
    // (NotificationsBell's `isApprover`) reads the RAW `is_store_owner` flag,
    // while the endpoint requires the store-scoped role — `isPrimaryAdmin()`
    // resolves the CURRENT store's `store_user` seat and demands ROLE_OWNER. A
    // user who owns one store but is acting in another passes the client gate
    // and fails the server's, so this polled a guaranteed 403 every 60s, twice
    // over (the queryClient default is `retry: 1`), on every admin page, for as
    // long as the tab stayed open.
    //
    // A 403 here is a settled answer, not a blip: re-asking cannot change it
    // within the session. Transient failures (network, 500) keep their retry and
    // their polling, so a real outage still recovers on its own. Reloading the
    // page — or switching store, which is the thing that would change the answer
    // — starts a fresh query and asks again.
    refetchInterval: (_data, query) => (isForbidden(query?.state?.error) ? false : 60_000),
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 1,
    queryFn: async () => {
      const { data } = await axios.get("/api/listing-change-requests", {
        metadata: { silent: true },
      });
      return data ?? { items: [], count: 0 };
    },
  });

export const requestListingChange = async (listingId, changes, orderId = null) => {
  const { data } = await axios.post(`/api/listings/${listingId}/change-requests`, {
    changes,
    order_id: orderId,
  });
  return data;
};

export const approveListingChange = async (id, note = null) => {
  const { data } = await axios.put(`/api/listing-change-requests/${id}/approve`, {
    review_note: note,
  });
  return data;
};

export const rejectListingChange = async (id, note = null) => {
  const { data } = await axios.put(`/api/listing-change-requests/${id}/reject`, {
    review_note: note,
  });
  return data;
};

/** Refresh both the per-listing banner and the bell after any decision. */
export const useRefreshChangeRequests = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["listing-change-requests"] });
};
